import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';
import {useSelector} from 'react-redux';

import {Section} from 'constants/NavConstants';
import {SearchSection} from 'graphql-client/__generated/graphqlGlobalTypes';
import {BenchlingState} from 'redux-stores/reduceApp';
import {SearchUISelector} from 'search/reduceSearchUI';
import {SavedSearchCard} from 'search/saved-searches/SavedSearchCard';
import {SavedSearchOptionsMenu} from 'search/saved-searches/SavedSearchOptionsMenu';
import {
  convertFromGraphqlSavedSearchParams,
  getMetadataText,
  SavedSearch,
} from 'search/saved-searches/SavedSearchUtil';
import {SearchParams} from 'search/SearchConstants';
import {LEGACY_FONT_PROXIMA_18} from 'styles/FontConstants';
import {GRAY_DARK, GRAY_DARKER, GRAY_LIGHT} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {OptionData, OptionValue} from 'ui-library/menus/MenuConstants';

export interface SavedSearchModalMenuSectionProps {
  savedSearchOptionData: Array<OptionData>;
  title: String;
  searchSection: SearchSection;
  closeModal: () => void;
  onSearch: (searchParams: SearchParams) => void;
  searchParams: SearchParams;
  refetchSavedSearches: () => Promise<void>;
  showOptionsMenu?: boolean;
  closeOnSelect?: boolean;
  selectedValue?: string;
  setSelectedValue?: (value: string) => void;
  onSelect?: (value: OptionValue) => void;
  requiredOwnerId?: string;
}

export default function SavedSearchModalMenuSection(props: SavedSearchModalMenuSectionProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(true);

  // We need to get the activeSavedSearch here, instead of just using a prop passed through from withSearching, since
  // sometimes when the modal is open, and the user is doing some operations to the saved searches, withSearching doesn't enhanceProps
  // so the activeSavedSearch will be outdated.
  // Only for Global Search does the graphql SearchSection not match up with the Section in NavConstants
  const searchSessionId = props.searchSection === SearchSection.GLOBAL ? Section.SEARCH : props.searchSection;
  const activeSavedSearch = useSelector((state: BenchlingState) =>
    new SearchUISelector(state).getActiveSavedSearchForSearchSession(searchSessionId)
  );

  // When in the modal, the active saved search in the background shouldn't be edited,
  // except when the SavedSearchCard being edited in the one that is active.
  const shouldSelect = (targetSavedSearch: SavedSearch): boolean =>
    activeSavedSearch != null && activeSavedSearch.name === targetSavedSearch.name;

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.searchTitle)}>
        <div
          className={css(styles.toggle)}
          onClick={() => {
            setIsOpen(!isOpen);
          }}
        >
          <BenchlingIcon className={css(styles.icon)} icon={isOpen ? 'chevron-down' : 'chevron-up'} />
          {props.title}
        </div>
        <div className={css(styles.horizontal)} />
      </div>
      {isOpen && (
        <div className="menu-xxlWidth">
          {props.savedSearchOptionData.map((optionData, index) => (
            <SavedSearchCard
              isDisabled={optionData.disabled}
              sharedIcon={optionData.value.collaboratorsJson.length > 0}
              selected={optionData.value.id === props.selectedValue}
              onSelect={() => {
                if (props.setSelectedValue) {
                  props.setSelectedValue(optionData.value.id);
                }
                if (props.onSelect) {
                  props.onSelect(optionData.value);
                  props.closeModal();
                }
              }}
              key={index}
              value={optionData.value}
              name={optionData.name}
              metadata={getMetadataText(
                optionData.value.description,
                optionData.value.ownerJson.name,
                optionData.value.modifiedAt,
                optionData.disabled,
                false,
                props.requiredOwnerId
              )}
              savedSearchOptionsMenu={
                !props.showOptionsMenu ? undefined : optionData.disabled ? (
                  <BenchlingIcon className={classNames(css(styles.optionsIcon))} icon="options" />
                ) : (
                  <SavedSearchOptionsMenu
                    searchSection={props.searchSection}
                    onSaved={(savedSearch?) => {
                      if (shouldSelect(optionData.value) && props.onSelect) {
                        props.onSelect(savedSearch);
                      }
                      props.refetchSavedSearches();
                    }}
                    onDeleted={() => {
                      if (shouldSelect(optionData.value) && props.onSelect) {
                        props.onSelect(undefined);
                      }
                      props.refetchSavedSearches();
                    }}
                    onSearch={props.onSearch}
                    targetSavedSearch={optionData.value}
                    searchParams={{
                      ...props.searchParams,
                      ...convertFromGraphqlSavedSearchParams(optionData.value.searchParams),
                    }}
                    hasUnsavedChanges={true}
                    hasUpdate={false}
                    hasReset={false}
                    dropLeft={true}
                  />
                )
              }
              dataTestAttrs={optionData.dataTestAttrs}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  searchTitle: {
    ...LEGACY_FONT_PROXIMA_18,
    color: GRAY_DARKER,
    display: 'flex',
    alignItems: 'center',
    marginBottom: 16,
  },
  horizontal: {
    marginLeft: 10,
    marginRight: 20,
    textAlign: 'center',
    borderBottom: `1.5px solid ${GRAY_LIGHT}`,
    flex: 1,
  },
  icon: {
    marginRight: 8,
  },
  toggle: {
    cursor: 'pointer',
  },
  container: {
    marginBottom: 16,
  },
  optionsIcon: {
    fontSize: '14px',
    color: GRAY_DARK,
    marginLeft: 6,
    whiteSpace: 'nowrap',
  },
});
